import React from 'react';
import { i18n } from '../../i18n/i18n';
import { KidButton, KidCard, KidChip, StarChip } from './KidUI';
import { StoredPicture } from './StoredPicture';
import { pluralKey } from '../../utils/starGoal';

/**
 * The task card used on Home and Tasks (kid-first redesign, mock-ups phone-01/02): picture,
 * title, room + star chips, and exactly one main action. Phone: picture beside the title;
 * from 768 px: a wide picture on top.
 */

function parseDueMs(task: any): number | null {
  const raw = task?.due_date;
  if (raw == null || raw === '') return null;
  const ms = new Date(raw).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/**
 * When the task is due, in calendar days: overdue / today (urgent, red) or tomorrow / in N days
 * (2026-10-13: Home lists every open task, so children need to see which ones can wait).
 */
export function dueLabel(task: any, now = Date.now()): { text: string; urgent: boolean } | null {
  const ms = parseDueMs(task);
  if (ms == null) return null;
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  const sod = d.getTime();
  if (ms < sod) return { text: i18n.t('dashboard.due_overdue'), urgent: true };
  const due = new Date(ms);
  due.setHours(0, 0, 0, 0);
  const days = Math.round((due.getTime() - sod) / (24 * 60 * 60 * 1000)); // DST-safe: whole days
  if (days === 0) return { text: i18n.t('dashboard.due_today'), urgent: true };
  if (days === 1) return { text: i18n.t('dashboard.due_tomorrow'), urgent: false };
  return { text: i18n.t(pluralKey('dashboard.due_in_days', days)).replace('{{n}}', String(days)), urgent: false };
}

export const starsText = (n: number) => i18n.t(pluralKey('common.stars', n)).replace('{{n}}', String(n));

export interface KidTaskCardProps {
  task: any;
  roomName?: string | null;
  /** The room's picture, shown in the room chip so children recognise the room (2026-10-13). */
  roomImage?: string | null;
  actionLabel: string;
  onAction: () => void;
  actionVariant?: 'primary' | 'secondary';
  actionTrailing?: React.ReactNode;
  /** Small text link under the action (e.g. "See details"). */
  linkLabel?: string;
  onLink?: () => void;
  /** Extra line under the chips (e.g. who the task belongs to). */
  footer?: React.ReactNode;
  busy?: boolean;
}

export const KidTaskCard: React.FC<KidTaskCardProps> = ({
  task, roomName, roomImage, actionLabel, onAction, actionVariant = 'primary', actionTrailing, linkLabel, onLink, footer, busy,
}) => {
  const due = dueLabel(task);
  return (
    <KidCard className="flex flex-col gap-4">
      <div className="flex md:flex-col items-center md:items-stretch gap-4">
        <StoredPicture url={task.header_image_url} imageId={task.header_image} label={task.title} size={88} className="md:hidden" fit="cover" />
        <div className="hidden md:block w-full h-44 lg:h-52">
          <StoredPicture url={task.header_image_url} imageId={task.header_image} label={task.title} size="fill" fit="cover" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-kid-heading font-extrabold text-2xl md:text-[28px] leading-tight break-words hyphens-auto">{task.title}</h3>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            {roomName && (
              <KidChip className={roomImage ? '!pl-1' : ''}>
                {roomImage && <img src={roomImage} alt="" className="inline-block w-8 h-8 rounded-full object-cover mr-1.5 align-middle border border-kid-border" />}
                {roomName}
              </KidChip>
            )}
            <StarChip label={starsText(Number(task.points) || 0)} />
            {due && (
              <KidChip className={due.urgent ? '!bg-red-100 !text-red-800 dark:!bg-red-900/40 dark:!text-red-200' : ''}>
                <span aria-hidden className="mr-1">⏰</span>{due.text}
              </KidChip>
            )}
          </div>
          {footer}
          {task.not_done_reason && !task.is_completed && (
            <p className="mt-2 rounded-kid-tile bg-kid-peach dark:bg-amber-900/40 px-3 py-2 font-bold">
              {i18n.t('approve.from_grownup').replace('{{reason}}', i18n.t(`approve.reason_${task.not_done_reason}`))}
            </p>
          )}
        </div>
      </div>
      <div className="space-y-1">
        <KidButton size="lg" fullWidth variant={actionVariant} disabled={busy} onClick={onAction} trailing={actionTrailing}>
          {busy ? '…' : actionLabel}
        </KidButton>
        {linkLabel && onLink && (
          <button type="button" onClick={onLink} className="block mx-auto min-h-[44px] px-3 font-bold underline text-kid-green-shadow dark:text-green-300">
            {linkLabel}
          </button>
        )}
      </div>
    </KidCard>
  );
};

export default KidTaskCard;
